import { Head, router } from "@inertiajs/react"
import { Button, Card, Flex, Heading, Table } from "@radix-ui/themes"
import { Plus, Trash2 } from "lucide-react"
import { z } from "zod"

import LinkButton from "@/components/link-button"
import StatusBadge from "@/components/status-badge"
import ProjectLayout from "@/layouts/project-layout"
import { formatDateTime } from "@/lib/datetime"
import { newProjectNewsletterPath, projectDashboardPath, projectNewsletterPath } from "@/routes"
import { NewsletterSchema, ProjectSchema } from "@/schemas"

const PropsSchema = z.object({
  project: ProjectSchema,
  newsletters: z.array(NewsletterSchema),
})

type Props = z.infer<typeof PropsSchema>

export default function NewslettersIndex(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { newsletters, project } = validatedProps

  const handleDelete = (newsletterId: number, newsletterName: string) => {
    if (
      confirm(`Are you sure you want to delete "${newsletterName}"? This action cannot be undone.`)
    ) {
      router.delete(`/projects/${project.slug}/newsletters/${newsletterId}`)
    }
  }

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Newsletters" },
      ]}
    >
      <Head title="Newsletters" />

      <Flex direction="column" gap="6" p="6">
        <Flex justify="between" align="center">
          <Heading size="6" weight="medium">
            Newsletters
          </Heading>

          <LinkButton href={newProjectNewsletterPath(project.slug)} variant="classic">
            <Plus className="mr-1 h-4 w-4" />
            New newsletter
          </LinkButton>
        </Flex>

        <Card>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Email Template</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {newsletters.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={5} style={{ textAlign: "center", color: "var(--gray-11)" }}>
                    No newsletters found
                  </Table.Cell>
                </Table.Row>
              ) : (
                newsletters.map((newsletter) => (
                  <Table.Row key={newsletter.id}>
                    <Table.RowHeaderCell
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() =>
                        router.visit(projectNewsletterPath(project.slug, newsletter.id))
                      }
                    >
                      {newsletter.name}
                    </Table.RowHeaderCell>
                    <Table.Cell
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() =>
                        router.visit(projectNewsletterPath(project.slug, newsletter.id))
                      }
                    >
                      {newsletter.email?.subject ?? "No email selected"}
                    </Table.Cell>
                    <Table.Cell
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() =>
                        router.visit(projectNewsletterPath(project.slug, newsletter.id))
                      }
                    >
                      <StatusBadge
                        status={newsletter.status}
                        color={
                          newsletter.status === "sent"
                            ? "green"
                            : newsletter.status === "sending"
                              ? "blue"
                              : newsletter.status === "failed"
                                ? "red"
                                : "gray"
                        }
                      />
                    </Table.Cell>
                    <Table.Cell
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() =>
                        router.visit(projectNewsletterPath(project.slug, newsletter.id))
                      }
                    >
                      {formatDateTime(newsletter.created_at)}
                    </Table.Cell>
                    <Table.Cell>
                      <Button
                        size="1"
                        variant="soft"
                        color="red"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDelete(newsletter.id, newsletter.name)
                        }}
                      >
                        <Trash2 className="mr-1 h-3 w-3" />
                        Delete
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Card>
      </Flex>
    </ProjectLayout>
  )
}
